import { formatMinutes } from '../Attendance/Reports/reportCell';
import {
  ADOPTED_SYSTEM_KEYS,
  LOCAL_ACCEPTANCE_SYSTEM_KEYS,
  SYSTEM_DEFINITION_FIELDS,
  getProcessDefinitionDisplay,
  getProcessDefinitionFieldLabel,
  getProcessDefinitionFieldOptions,
  getProcessDefinitionFieldText,
} from '../Processes/processDisplay';

export const SUPPORTED_PROCESS_FORM_TYPES = [
  'text_field',
  'textarea_field',
  'number_field',
  'currency_field',
  'date_picker',
  'select_dropdown',
  'organization_location',
  'file_attachment',
  // 2026-08-25. These three were added to `form_validation.SUPPORTED_FORM_FIELD_TYPES`
  // on 08-24 and this mirror was not updated, so the clients declared eight while the
  // engine accepted eleven. Pinned against `schemas/platform-openapi.json` now, so the
  // list cannot drift silently again.
  'worker_reference',
  'department_reference',
  'static_text',
  'place_field',
  // 2026-08-26. The engine's only LIST-valued field, and a duration stored as INTEGER
  // MINUTES. Mirrored from `form_validation.SUPPORTED_FORM_FIELD_TYPES`; the coverage gate
  // derives the list from the emitted schema, so this array cannot drift from it silently.
  'multi_select',
  'duration_field',
] as const;

/** The seeded processes a worker can start, and the ONLY list of them.
 *
 * Until 2026-09-02 the three non-acceptance keys read their copy from `worker:systemStarters`,
 * which was a BYTE-IDENTICAL duplicate of `processes:liveDisplay.systemDefinitions` in all nine
 * locales, while `local_acceptance_*` already delegated to that table. Two sources for one thing
 * (svdy-rules §10.5b), and the cost was concrete: the eight definitions adopted into the seed on
 * 2026-08-24 had to be added to BOTH to show up localized, so they were added to neither.
 * The duplicate is gone and every starter now delegates.
 */
export const SYSTEM_STARTER_KEYS = [
  'general_approval',
  'purchase_request',
  'it_access_request',
  ...ADOPTED_SYSTEM_KEYS,
  ...LOCAL_ACCEPTANCE_SYSTEM_KEYS,
] as const;

export type SystemStarterKey = (typeof SYSTEM_STARTER_KEYS)[number];
export type ProcessFormFieldKind =
  | 'text' | 'textarea' | 'number' | 'currency' | 'date' | 'select'
  | 'location' | 'attachment'
  // 2026-08-24. `worker` / `department` are pickers over the tenant's own rows —
  // the same shape as `location`, resolved and snapshotted server-side.
  // `staticText` is NOT an input: it collects nothing, so a renderer that draws it
  // as a disabled field misrepresents it.
  | 'worker' | 'department' | 'staticText'
  // 2026-08-24. A free-text PLACE with autocomplete on top. It reuses the kind NAME the
  // attendance request form already uses for its destination fields, so two modules
  // share one vocabulary for one concept.
  | 'place'
  // 2026-08-26. `multiSelect` is the ONLY kind whose value is an array, so every consumer
  // that reads a value has to branch on it rather than on a property (see the backend's
  // note on why this is a type and not `multiple: true`). `duration` is a number of
  // minutes wearing an `h:mm` control.
  | 'multiSelect' | 'duration'
  | 'unsupported';

export type ProcessFormFieldLike = {
  id?: string;
  type?: string;
  component_id?: string;
  label?: string;
  required?: boolean;
  properties?: {
    label?: string;
    options?: string[] | { label: string; value: string }[];
    currency?: string;
    document_kind?: string;
    accept?: string;
    max_count?: number;
    max_bytes?: number;
    [key: string]: unknown;
  };
};

export type ProcessFormSchemaLike = {
  system_key?: string | null;
  origin_system_key?: string | null;
  fields?: ProcessFormFieldLike[];
};

type StarterCarrier = {
  system_key?: string | null;
  form_schema?: unknown;
};

type ProcessLike = StarterCarrier & {
  name?: string | null;
  description?: string | null;
};

type Translate = (key: string) => string;

export const SYSTEM_STARTER_FIELD_IDS: Record<SystemStarterKey, readonly string[]> = Object.fromEntries(
  SYSTEM_STARTER_KEYS.map((key) => [key, SYSTEM_DEFINITION_FIELDS[key]]),
) as Record<SystemStarterKey, readonly string[]>;

/** Backend twin: app/processes/form_validation.py::_field_key. */
export function processFormFieldKey(field: ProcessFormFieldLike, index: number): string {
  return field.id || field.properties?.label || field.label || `field_${index}`;
}

export function processFormFieldKind(type: string | undefined): ProcessFormFieldKind {
  const code = (type ?? '').trim().toLowerCase().replace(/[- ]/g, '_');
  if (['text', 'text_field'].includes(code)) return 'text';
  if (['textarea', 'textarea_field', 'multi_line_text', 'long_text'].includes(code)) return 'textarea';
  if (['number', 'number_field', 'number_input'].includes(code)) return 'number';
  if (['currency', 'currency_field', 'currency_input', 'money', 'amount'].includes(code)) return 'currency';
  if (['date', 'date_picker'].includes(code)) return 'date';
  if (['select', 'dropdown', 'select_dropdown'].includes(code)) return 'select';
  if (['organization_location', 'location'].includes(code)) return 'location';
  if (code === 'file_attachment') return 'attachment';
  // The aliases mirror `form_validation._TYPE_ALIASES`, so a definition saved by an
  // older client or seeded by hand resolves the same way here as on the server.
  if (['worker_reference', 'worker', 'employee', 'person'].includes(code)) return 'worker';
  if (['department_reference', 'department'].includes(code)) return 'department';
  if (['static_text', 'description', 'note'].includes(code)) return 'staticText';
  if (['place_field', 'place', 'address', 'destination'].includes(code)) return 'place';
  if (['multi_select', 'multiselect', 'multi_select_dropdown', 'checkbox_group'].includes(code)) return 'multiSelect';
  if (['duration_field', 'duration'].includes(code)) return 'duration';
  return 'unsupported';
}

/**
 * Does this date field also ask for a time?
 *
 * Backend twin: `form_validation.date_field_includes_time`. When true the value is
 * `YYYY-MM-DDTHH:MM` (naive local, the convention the attendance request form already
 * uses for its `HH:MM` times) and the server rejects a date without one.
 */
export function dateFieldIncludesTime(field: ProcessFormFieldLike): boolean {
  return (field.properties as Record<string, unknown> | undefined)?.include_time === true;
}

/**
 * A stored duration (INTEGER MINUTES) as `2:30` / `45m`.
 *
 * Delegates to the attendance reports' `formatMinutes` rather than re-implementing it.
 * That function is already the app's answer to "how do we write a length of time", and a
 * second one would drift the moment either changed (§10.5b) — the same reason the backend
 * stores minutes instead of letting each tenant pick a unit.
 */
export function formatDurationMinutes(value: unknown): string {
  const minutes = typeof value === 'number'
    ? value
    : typeof value === 'string' && /^-?\d+$/.test(value.trim())
      ? Number(value.trim())
      : null;
  if (minutes === null || !Number.isFinite(minutes)) return '';
  return formatMinutes(minutes);
}

/**
 * Parse what a requester typed into INTEGER MINUTES, or null when it is not a duration.
 *
 * Accepts the same two shapes the server accepts (`2:30` and a plain minute count), so the
 * client cannot be stricter than the endpoint and produce a field that looks broken while
 * the API would have taken the value.
 */
export function parseDurationMinutes(text: string): number | null {
  let raw = text.trim();
  if (!raw) return null;
  // Accept what the app itself DISPLAYS. `formatDurationMinutes(45)` is `45m`, so a
  // requester who reads the value and retypes it was getting an error on the app's own
  // output — the display-must-equal-submit defect (svdy-mobile-ux §9.1), found by a
  // round-trip assertion rather than by reading either function.
  //
  // Only the suffix is stripped, and only here: the value SUBMITTED is always a number of
  // minutes, so the server never sees `45m` and this cannot make the client more
  // permissive than the endpoint.
  const suffixed = /^(\d+)\s*m(in|ins|inutes?)?$/i.exec(raw);
  if (suffixed) raw = suffixed[1];
  if (raw.includes(':')) {
    const parts = raw.split(':');
    // Exactly two parts. `raw.split(':')` on `2:30:00` yields three and a destructuring
    // read of the first two silently DROPPED the seconds and stored 2:30 — throwing away
    // something the requester typed without saying so, which this codebase already refuses
    // to do for a date field that is handed a time. The server rejects `2:30:00` outright,
    // so accepting it here also made the client disagree with the endpoint.
    if (parts.length !== 2) return null;
    const [head, tail = ''] = parts;
    const hours = Number(head);
    const minutes = Number(tail);
    if (!Number.isInteger(hours) || !Number.isInteger(minutes)) return null;
    if (hours < 0 || minutes < 0 || minutes > 59) return null;
    return hours * 60 + minutes;
  }
  const minutes = Number(raw);
  if (!Number.isInteger(minutes) || minutes < 0) return null;
  return minutes;
}

/** The stored value of a multi-select, as a list of strings. */
export function multiSelectValues(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string');
  // A bare string is what a control switched from single to multi can still hold, and the
  // server widens it the same way — so the two sides agree on what one answer looks like.
  if (typeof value === 'string' && value.trim()) return [value];
  return [];
}

/** Split a stored `YYYY-MM-DDTHH:MM` into its two halves for two separate controls. */
export function splitDateTime(value: unknown): { day: string; time: string } {
  const text = typeof value === 'string' ? value.trim() : '';
  const [day = '', time = ''] = text.split('T');
  return { day, time: time.slice(0, 5) };
}

/** Rejoin them. Empty until BOTH halves are set, so a half-filled field submits nothing
 *  rather than a date the server will reject for missing its time. */
export function joinDateTime(day: string, time: string): string {
  return day && time ? `${day}T${time}` : '';
}

/**
 * Fields the SERVER computes from two date fields, keyed by submit key.
 *
 * Backend twin: `form_validation.derived_day_fields`. The property is declared on the
 * schema, so a client no longer has to GUESS which number is the day count — this one
 * used to match `/(days|duration)…/` against the field's id and label, which is a
 * heuristic that can fire on the wrong field and miss the right one.
 *
 * `resolve_authoritative_form_values` overwrites whatever is submitted for these, so a
 * client that renders one as editable is showing the requester a number that will be
 * discarded. Render it read-only.
 */
export function derivedDayFields(
  schema: ProcessFormSchemaLike | null | undefined,
): Record<string, [string, string]> {
  const fields = Array.isArray(schema?.fields) ? schema!.fields! : [];
  const keyById: Record<string, string> = {};
  fields.forEach((field, index) => {
    if (field.id) keyById[String(field.id)] = processFormFieldKey(field, index);
  });
  const derived: Record<string, [string, string]> = {};
  fields.forEach((field, index) => {
    const spec = (field.properties as Record<string, unknown> | undefined)?.derived_from_dates;
    if (!Array.isArray(spec) || spec.length !== 2) return;
    const start = keyById[String(spec[0])];
    const end = keyById[String(spec[1])];
    if (start && end) derived[processFormFieldKey(field, index)] = [start, end];
  });
  return derived;
}

/**
 * How a department is written wherever a requester chooses or reviews one.
 *
 * The parent prefix is the point: an SMB org chart repeats leaf names, so two
 * "Field" rows under different parents are otherwise the same row twice and picking
 * the wrong one mis-routes silently. One function, because the picker, the picked
 * value and the review line all show it and three copies would drift.
 *
 * Mobile/web twins — update in lockstep (svdy-ui §13).
 */
export function departmentDisplayName(
  department: { name?: string | null; parent_name?: string | null } | null | undefined,
): string | undefined {
  const name = department?.name?.trim();
  if (!name) return undefined;
  const parent = department?.parent_name?.trim();
  return parent ? `${parent} › ${name}` : name;
}

export function systemStarterKey(source: StarterCarrier | null | undefined): SystemStarterKey | undefined {
  // A template's key, or a copy's server-managed origin. Either only ALLOWS translation: each string
  // is still compared against the built-in's English in processDisplay (`isSystemText`).
  const nestedSystemKey = source?.form_schema !== null && typeof source?.form_schema === 'object'
    ? (source.form_schema as ProcessFormSchemaLike).system_key
      ?? (source.form_schema as ProcessFormSchemaLike).origin_system_key
    : undefined;
  // A schema passed directly carries its origin at the top level.
  const candidate = source?.system_key ?? nestedSystemKey
    ?? (source as ProcessFormSchemaLike | null | undefined)?.origin_system_key;
  return SYSTEM_STARTER_KEYS.includes(candidate as SystemStarterKey)
    ? candidate as SystemStarterKey
    : undefined;
}

/**
 * The provenance `processDisplay` decides from: a template's key (all of its text is SVDY's) or a copy's
 * origin (only the strings still worded as the built-in). Passing the bare key would turn a copy into a
 * template and translate what the tenant edited.
 */
function starterSource(source: StarterCarrier | null | undefined, starter: SystemStarterKey) {
  // Top-level only: a key inside a schema (a request's or task's snapshot) is compared, not trusted.
  const trusted = source?.system_key === starter && !('fields' in (source ?? {}));
  return trusted
    ? { system_key: starter }
    : { form_schema: { origin_system_key: starter } };
}

export function localizedProcessName(process: ProcessLike, translate: Translate): string {
  const starter = systemStarterKey(process);
  if (!starter) return process.name ?? '';
  return getProcessDefinitionDisplay(translate, {
    ...starterSource(process, starter),
    name: process.name ?? '',
    description: process.description,
  }).name;
}

export function localizedProcessDescription(
  process: ProcessLike,
  translate: Translate,
): string | null | undefined {
  const starter = systemStarterKey(process);
  if (!starter) return process.description;
  return getProcessDefinitionDisplay(translate, {
    ...starterSource(process, starter),
    name: process.name ?? '',
    description: process.description,
  }).description;
}

function serverFieldLabel(field: ProcessFormFieldLike, index: number): string {
  return field.properties?.label || field.label || processFormFieldKey(field, index);
}

export function localizedProcessFieldLabel(
  schema: ProcessFormSchemaLike | null | undefined,
  field: ProcessFormFieldLike,
  index: number,
  translate: Translate,
): string {
  const starter = systemStarterKey(schema);
  if (!starter || !field.id || !SYSTEM_STARTER_FIELD_IDS[starter].includes(field.id)) {
    return serverFieldLabel(field, index);
  }
  return getProcessDefinitionFieldLabel(
    translate,
    { ...starterSource(schema, starter), name: '' },
    field,
    index,
  );
}

/** The BODY of a `static_text` field, localized.
 *
 * Two seeded processes carry one (`leave_request.leave_note`, `onboarding.onboarding_note`) and
 * both rendered as raw English inside an otherwise fully localized form, because a static_text
 * body is not a label and nothing translated it. Read-only surfaces only — the Designer's editor
 * must keep showing the STORED text, which is what the admin is editing.
 */
export function localizedProcessFieldText(
  schema: ProcessFormSchemaLike | null | undefined,
  field: ProcessFormFieldLike,
  translate: Translate,
): string {
  const stored = typeof field.properties?.text === 'string' ? field.properties.text : '';
  const starter = systemStarterKey(schema);
  if (!starter) return stored;
  return getProcessDefinitionFieldText(translate, { ...starterSource(schema, starter), name: '' }, field);
}

export function localizedProcessFieldOptions(
  schema: ProcessFormSchemaLike | null | undefined,
  field: ProcessFormFieldLike,
  translate: Translate,
): { label: string; value: string }[] {
  const options = field.properties?.options;
  if (!Array.isArray(options)) return [];
  const normalized = options.map((option) =>
    typeof option === 'string' ? { label: option, value: option } : option,
  );
  const starter = systemStarterKey(schema);
  if (!starter) return normalized;
  return getProcessDefinitionFieldOptions(translate, { ...starterSource(schema, starter), name: '' }, field);
}

export function localizedProcessFieldValue(
  schema: ProcessFormSchemaLike | null | undefined,
  field: ProcessFormFieldLike | undefined,
  value: unknown,
  translate: Translate,
): unknown {
  const starter = systemStarterKey(schema);
  if (!starter || !field || typeof value !== 'string') return value;
  const localized = localizedProcessFieldOptions(schema, field, translate)
    .find((option) => option.value === value);
  return localized?.label ?? value;
}

export type SubmittedFormRow = {
  /** The submit key, which is also the `field_id` an attachment is filed under. */
  key: string;
  value: unknown;
  field: ProcessFormFieldLike | undefined;
  label: string;
};

/**
 * A submitted application as ROWS, for any surface that displays one.
 *
 * Exported as a pure function on purpose. The admin instance page derived this inline and a
 * regression removed the attachment rows from it (2026-09-08 → 09-09): the filter was lifted from
 * MOBILE, where it is correct because `requests.tsx` renders `selected.attachments` as its own list,
 * and dropped into a page that has no such list. No field row → no `ProcessFormResourceValue` → the
 * file was never shown, while the endpoint kept sending it.
 *
 * A source-level assertion could not catch that, because the filter's own presence is what a grep
 * finds. Assert this function's RETURN VALUE instead (svdy-ui §16 rule 5).
 *
 * **No kind is excluded here.** `attachment` is a resource kind: the stored value is the attachment
 * id and `ProcessFormResourceValue` resolves it to a name, a size and a download. Excluding the row
 * is what hid the file; printing the raw value is what put `pat_01M202YDYS3K…` on a screen. Neither
 * is this function's business — it decides WHICH rows exist, not how a value reads.
 */
export function submittedFormRows(
  formData: unknown,
  formSchema: ProcessFormSchemaLike | null | undefined,
  translate?: (key: string) => string,
): SubmittedFormRow[] {
  if (!formData || typeof formData !== 'object' || Array.isArray(formData)) return [];
  const fields = Array.isArray(formSchema?.fields) ? formSchema!.fields! : [];
  return Object.entries(formData as Record<string, unknown>)
    .filter(([, value]) => value !== null && value !== undefined && value !== '')
    .map(([key, value]) => {
      // Id-or-index, the same resolution mobile's `fieldForKey` uses, so one submission
      // resolves to the same field on every surface.
      const index = fields.findIndex((candidate, at) => (candidate?.id ?? `field_${at}`) === key);
      const field = index >= 0 ? fields[index] : undefined;
      return {
        key,
        value,
        field,
        // LOCALIZED when a translator is supplied.
        //
        // Three of the five surfaces resolved this differently: the admin instance page used the raw
        // `properties.label`, the admin request modal called `getProcessDefinitionFieldLabel`, and the
        // two worker surfaces called `localizedProcessFieldLabel`. So one seeded definition showed
        // English labels on one admin page and translated ones on the next (2026-09-09 census).
        //
        // The translator is OPTIONAL rather than required because the row model is also used where no
        // `t` is in scope, and falling back to the schema's own label is the honest answer there — not
        // an empty cell. The raw key is the last resort, which is all a field the schema no longer
        // declares can be called.
        label: (translate && field
          ? localizedProcessFieldLabel(formSchema, field, index, translate)
          : '')
          || field?.properties?.label || field?.label || key,
      };
    });
}

/**
 * A submitted value, as the reader should see it — the ONE definition.
 *
 * Promoted here 2026-09-09 from a file-local `fieldValue` in `WorkerRequestDetail`. Before that there
 * were FOUR answers to "how does a submitted value read" across five surfaces, and the owner's report
 * was the arithmetic of that: the admin instance page and the admin request modal printed
 * `Estimated Cost 100` while the worker portal printed `100 USD`, because only the worker portal's
 * private copy appended the unit (screenshots 01/05 vs 04). The worker portal is the surface the owner
 * picked as the reference, so its version is the one that survives (svdy-ui §20).
 *
 * Two things it does that `formatPayloadValue` alone cannot:
 *
 *   - LOCALIZES a select's chosen option, via `localizedProcessFieldValue`. Without the schema and `t`
 *     a stored option value is printed raw, so a seeded process shows English inside a Chinese page.
 *   - APPENDS THE CURRENCY, for `currency_field` and for the legacy number fields whose label matches
 *     `/amount|budget|cost|value/i`. That heuristic is carried over deliberately: definitions seeded
 *     before `currency_field` existed store an amount as a plain number, and dropping the heuristic
 *     would silently remove the unit from those rows.
 *
 * A currency field with no unit is not a shorter value, it is an ambiguous one — which is why this is
 * grouped with the layout work rather than treated as polish.
 */
export function formatSubmittedValue(
  value: unknown,
  field: ProcessFormFieldLike | undefined,
  schema: ProcessFormSchemaLike | null | undefined,
  organizationCurrency: string | null | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  if (value == null || value === '') return '\u2014';
  const kind = processFormFieldKind(field?.type);
  if (kind === 'duration') {
    const formatted = formatDurationMinutes(value);
    return formatted || '\u2014';
  }
  if (kind === 'multiSelect') {
    const chosen = multiSelectValues(value);
    if (chosen.length === 0) return '\u2014';
    return chosen
      .map((entry) => String(localizedProcessFieldValue(schema, field, entry, t as never)))
      .join(', ');
  }
  const localized = localizedProcessFieldValue(schema, field, value, t as never);
  // An object reaching here means no kind claimed it. Say so rather than pasting a brace: every kind
  // that legitimately stores an object goes to `ProcessFormResourceValue`, and `[object Object]` on an
  // approver's screen is the defect this whole area was reported for.
  if (localized !== null && typeof localized === 'object') {
    return Array.isArray(localized)
      ? localized.map((item) => String(item)).join(', ')
      : t('processes:requestList.unsupportedValue');
  }
  const label = field?.properties?.label || field?.label || '';
  // Seeded-before-`currency_field` definitions store an amount as a plain number.
  const legacyCurrency = kind === 'number' && /amount|budget|cost|value/i.test(label);
  if (kind === 'currency' || legacyCurrency) {
    const currency = String(
      field?.properties?.currency ?? organizationCurrency ?? 'USD',
    ).toUpperCase();
    return `${String(localized)} ${currency}`;
  }
  return String(localized);
}

/**
 * The ONLY list of request-attachment formats, mirroring what the runtime actually accepts.
 *
 * Backend truth (`app/shared/storage.py::validate_process_attachment_bytes`): PDF, JPEG and PNG
 * only, verified by magic bytes, ≤10 MiB, and a file whose content does not match its declared
 * type is rejected. So an invoice can be a PDF **or** a photo — that part already worked.
 *
 * What did not: the Designer's `allowedTypes` property was a free-text comma field whose own
 * defaults included `docx` and `gif`, nothing read it, and the worker's file picker used two
 * separate hardcoded `accept` constants. Three sources for one rule, none of them agreeing with
 * the backend (svdy-rules §10.5b). An admin could "allow docx" and the worker would be rejected
 * at upload for obeying them.
 */
export const PROCESS_ATTACHMENT_FORMATS = [
  { value: 'pdf', mime: 'application/pdf' },
  { value: 'jpg', mime: 'image/jpeg' },
  { value: 'png', mime: 'image/png' },
] as const;

export type ProcessAttachmentFormat = (typeof PROCESS_ATTACHMENT_FORMATS)[number]['value'];

/** Normalize a stored `allowedTypes` list to formats the runtime can actually accept. */
export function attachmentFormats(
  allowed: unknown,
): ProcessAttachmentFormat[] {
  const supported = PROCESS_ATTACHMENT_FORMATS.map((format) => format.value);
  if (!Array.isArray(allowed) || allowed.length === 0) return [...supported];
  const normalized = allowed
    .map((value) => String(value).trim().toLowerCase())
    .map((value) => (value === 'jpeg' ? 'jpg' : value))
    .filter((value): value is ProcessAttachmentFormat =>
      (supported as string[]).includes(value));
  // An empty result means the saved list named nothing the runtime supports (a legacy `docx`
  // row). Falling back to everything supported is the honest read: the field still works, and
  // the Designer now cannot save such a list again.
  return normalized.length > 0 ? Array.from(new Set(normalized)) : [...supported];
}

/** The `accept` attribute for a file input, derived from the field's own allowed formats. */
export function attachmentAccept(allowed: unknown): string {
  const formats = attachmentFormats(allowed);
  return PROCESS_ATTACHMENT_FORMATS
    .filter((format) => formats.includes(format.value))
    .map((format) => format.mime)
    .join(',');
}
